import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { eqlCatalogues } from "../../../../apis/apiEQL";
import { useReportContext } from "../../../Report/ReportContext/useReportContext";
import useTagsCatalog from "../../../../hooks/useTagsCatalog";
import useCatalogValueLabels from "./useCatalogValueLabels";
import {
  DASH,
  FIXED_DIMENSION_DEFS,
  buildCatalogDimension,
  buildIndicatorDimension,
  buildTagFlagDimension,
} from "../utils/dimensions";

/**
 * The Builder's dimension machinery, extracted so the Custom Dashboards piece
 * editor can offer the same fields/labels without duplicating any of it.
 *
 * Returns the four dimension families (fixed / indicator / tag-flag / catalog)
 * resolved for the tenant + active language, the lookups the pivot needs, and
 * the label helpers materializeView() consumes. Owns catalog discovery and the
 * per-catalog hierarchy depth (used by both the query and the depth stepper).
 */
export const useBuilderDims = () => {
  const [t, i18n] = useTranslation("exports");
  const { status_locales, entryways, severityOptions, optionsMonth, indicatorOptions } =
    useReportContext();
  const tagsCatalog = useTagsCatalog(); // { [tagKey]: { key, label, locales } } from TG-100

  const [catalogDims, setCatalogDims] = useState([]);
  // Needs catalogDims: the hierarchical ones are the catalogs whose deep value
  // levels have to be fetched as a tree to be labeled at all.
  const { catalogValueLabel, labelsVersion: catalogLabelsVersion } =
    useCatalogValueLabels(catalogDims);
  // Catalog hierarchy depth is per-catalog: { [dimKey]: level }. Default 1.
  const [depthByCatalog, setDepthByCatalog] = useState({});
  const catalogDepth = (key) => depthByCatalog[key] ?? 1;

  // Catalog dimensions are discovered from the tenant's form.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const resp = await eqlCatalogues();
        if (!active) return;
        setCatalogDims((resp?.catalogues || []).map(buildCatalogDimension));
      } catch (e) {
        console.error("useBuilderDims: could not load EQL catalogues", e);
      }
    })();
    return () => {
      active = false;
    };
  }, []);

  // useReportContext rebuilds these option objects on every render, so a memo
  // keyed on them would never hit. Keep the label object in a ref: always
  // current, never a dependency.
  const labels = {
    dash: DASH,
    yes: t("builder.yes"),
    no: t("builder.no"),
    statusLabel: (k) => status_locales?.[k]?.label || String(k),
    entrywayLabel: (k) => entryways?.[k]?.label || String(k),
    severityLabel: (k) => severityOptions.find((o) => o.value === k)?.label || String(k),
    monthLabel: (mm) => optionsMonth.find((o) => o.value === mm)?.label || mm,
    closedTypeLabel: (k) =>
      k == null || k === ""
        ? t("builder.closed_in_process")
        : t(`builder.closed_${k}`, String(k)),
    catalogLabel: catalogValueLabel,
  };
  const labelsRef = useRef(labels);
  labelsRef.current = labels;

  const indicatorDims = indicatorOptions.map((o) =>
    buildIndicatorDimension(o.key.replace(/^indicator_/, ""), o.label, o.iconName)
  );

  const tagLabelOf = (tag) => tag?.locales?.[i18n.language] || tag?.label || tag?.key;
  const tagFlagDims = Object.values(tagsCatalog || {}).map((tag) =>
    buildTagFlagDimension(tag.key, tagLabelOf(tag))
  );

  const indicatorSig = indicatorDims.map((d) => d.title).join("|");
  const tagFlagSig = tagFlagDims.map((d) => `${d.key}:${d.title}`).join("|");

  const dims = useMemo(
    () => [
      ...FIXED_DIMENSION_DEFS.map((d) => ({ ...d, title: t(d.titleKey) })),
      ...indicatorDims,
      ...tagFlagDims,
      ...catalogDims,
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [catalogDims, t, indicatorSig, tagFlagSig]
  );

  const dimByKey = useMemo(
    () => Object.fromEntries(dims.map((d) => [d.key, d])),
    [dims]
  );

  const fields = useMemo(
    () =>
      dims.map((d) => ({
        key: d.key,
        title: d.title,
        isCatalog: !!d.isCatalog,
        isIndicator: !!d.isIndicator,
        isTagFlag: !!d.isTagFlag,
        hierarchical: !!d.hierarchical,
        iconName: d.iconName,
        tagKey: d.tagKey,
      })),
    [dims]
  );

  const fieldTitle = (key) => dimByKey[key]?.title || key;

  return {
    dims,
    dimByKey,
    catalogDims,
    fields,
    fieldTitle,
    labels,
    labelsRef,
    depthByCatalog,
    setDepthByCatalog,
    catalogDepth,
    catalogLabelsVersion,
  };
};

export default useBuilderDims;
